import React from 'react';
import SelectMui, { SelectProps as MuiSelectProps } from '@mui/material/Select';
import FormControl, { FormControlProps } from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import type { MenuProps as MuiMenuProps } from '@mui/material/Menu';
import InputLabel, { InputLabelProps } from '@mui/material/InputLabel';
import FormHelperText from '@mui/material/FormHelperText';
import helperTextError from '@/components/mui/Form/TextField/helperTextError';
import elements from '@/components/elements';

const ITEM_HEIGHT = 36;

const menuProps: Partial<MuiMenuProps> = {
  slotProps: {
    paper: {
      style: {
        maxHeight: ITEM_HEIGHT * 8.5,
      },
    },
  },
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },
};

interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

// TODO: type this on its value. `MuiSelectProps` without a type argument makes
// `onChange` hand back `unknown`, which every caller then narrows by hand.
interface SelectBaseProps extends Omit<MuiSelectProps, 'label'> {
  // Optional: a caller that passes `renderOptions` builds the list itself. The
  // country selects rule their head off with a divider, which a flat list of
  // options cannot express.
  options?: SelectOption[];
  // Optional: four selects in the profile notifications tab are labelled by the
  // row they sit in, not by the field. A node, because callers pass a
  // `<FormattedMessage/>` as readily as a formatted string.
  label?: React.ReactNode;
  renderOptionLabel?: (option: SelectOption) => React.ReactNode;
  renderOptions?: () => React.ReactNode;
  id?: string;
  FormControlProps?: FormControlProps;
  InputLabelProps?: InputLabelProps;
  inputProps?: Record<string, any>;
  helperText?: string;
}

const SelectBase = React.forwardRef<any, SelectBaseProps>(
  (
    {
      label,
      options = [],
      renderOptionLabel,
      renderOptions,
      id,
      FormControlProps = {},
      InputLabelProps = {},
      inputProps = {},
      helperText,
      ...props
    },
    ref,
  ) => (
    <FormControl {...FormControlProps}>
      <InputLabel id={id} {...InputLabelProps}>
        {label}
      </InputLabel>
      <SelectMui
        ref={ref}
        labelId={id}
        label={label}
        MenuProps={menuProps}
        inputProps={{ ...inputProps, 'data-element': elements.popup.Upload.categorySelector.input }}
        {...props}
      >
        {typeof renderOptions === 'function'
          ? renderOptions()
          : options.map((o) => (
              <MenuItem
                key={`${o.value}-${o.label}`}
                value={o.value}
                disabled={o?.disabled || false}
                data-element={elements.popup.Upload.categorySelector.listItem}
              >
                {typeof renderOptionLabel === 'function' ? renderOptionLabel(o) : o.label}
              </MenuItem>
            ))}
      </SelectMui>
      {helperText && (
        <FormHelperText>
          {helperTextError(helperText)}
        </FormHelperText>
      )}
    </FormControl>
  ),
);

SelectBase.displayName = 'SelectBase';

export type { SelectBaseProps, SelectOption };

export default SelectBase;
